import React, { useContext, useEffect, useState } from "react";
import { validatePhoneNumber } from "manifest-core/utils/phone";
import scrollToTop from "manifest-core/utils/scrollToTop";
import { createFieldValidations } from "manifest-components/src/Form/Form";
import Signup from "manifest-core/classes/employerDashboard/signup";
import { AppContext } from "../../context/App/appProvider";
import { CreateAccountScreen } from "./CreateAccountScreen";
import { route, RoutePaths, useTypedNavigate } from "../../routes/RoutePaths";

export type CreateAccountPageControllerType = ReturnType<typeof CreateAccountPageController>;

function CreateAccountPageController() {
  const { createAccount, login } = useContext(AppContext);
  const navigate = useTypedNavigate();

  const [accountDetails, setAccountDetails] = useState(new Signup());
  const [passwordValid, setPasswordValid] = useState(false);
  const [confirmPasswordValid, setConfirmPasswordValid] = useState(false);
  const [error, setError] = useState(false);
  const [isDisclaimerChecked, setIsDisclaimerChecked] = useState(false);

  const fieldValidations = createFieldValidations(
    {
      ...accountDetails,
      isDisclaimerChecked,
    },
    {
      firstName: {
        deps: ["firstName"],
        validation: () => {
          if (!accountDetails.firstName.length) {
            return "Please enter your legal first name";
          }
        },
      },
      lastName: {
        deps: ["lastName"],
        validation: () => {
          if (!accountDetails.lastName.length) {
            return "Please enter your last name";
          }
        },
      },
      companyName: {
        deps: ["companyName"],
        validation: () => {
          if (!accountDetails.companyName.length) {
            return "Please enter your company name";
          }
        },
      },
      workEmail: {
        deps: ["workEmail"],
        validation: () => {
          const regexpEmail =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
          const valid = regexpEmail.test(accountDetails.email) && accountDetails.email.length > 0;
          if (!valid) {
            return "Please enter a valid email in format: example@email.com";
          }
        },
      },
      phoneNumber: {
        deps: ["phoneNumber"],
        validation: () => {
          if (!validatePhoneNumber(accountDetails.phoneNumber)) {
            return "Please enter a valid 10-digit phone number.";
          }
        },
      },
      password: {
        deps: [],
        validation: () => {
          if (!accountDetails.password.length) return "Please enter a password";
        },
      },
      confirmPassword: {
        deps: [],
        validation: () => {
          if (!confirmPasswordValid) return "Invalid password";
        },
      },
      disclaimerChecked: {
        deps: [],
        validation: () => {
          if (!isDisclaimerChecked) {
            return "Please accept the disclaimer";
          }
        },
      },
    }
  );

  const handleCreateAccount = async () => {
    try {
      await createAccount(accountDetails);
      const user = await login(accountDetails);
      if (!user.emailVerified) {
        return navigate(route(RoutePaths.accountVerifyEmail));
      }
      return navigate(route(RoutePaths.setup));
    } catch (err) {
      scrollToTop();
      setError(true);
    }
  };

  const handleContinue = () => {
    const validated = fieldValidations.validate("all");
    if (validated) {
      return handleCreateAccount();
    }
  };

  const updateAccountDetail = (field: keyof typeof accountDetails) => (val: any) => {
    setAccountDetails(
      new Signup({
        ...accountDetails,
        [field]: val,
      })
    );
  };

  // Validate fields
  useEffect(() => {
    fieldValidations.validate();
  }, [passwordValid, confirmPasswordValid]);

  useEffect(() => {
    if (fieldValidations.validatingField.disclaimerChecked) {
      fieldValidations.validateField("disclaimerChecked");
    }
  }, [isDisclaimerChecked, fieldValidations.validatingField.disclaimerChecked]);

  return {
    accountDetails,
    setPasswordValid,
    setConfirmPasswordValid,
    updateAccountDetail,
    fieldValidations,
    allowContinue: fieldValidations.isEverythingValid,
    allowCreate: fieldValidations.isEverythingValid,
    createAccount: handleCreateAccount,
    error,
    handleContinue,
    isDisclaimerChecked,
    LEGAL_DISCLAIMER: undefined,
    setIsDisclaimerChecked,
  };
}

export default function CreateAccountPage() {
  return <CreateAccountScreen {...CreateAccountPageController()} />;
}
